// daemon-discovery.js — Loopback fetch + HMAC import flow.
//
// The local daemon listens on 127.0.0.1:7777-7790. This module owns
// every fetch() to that loopback address: discovering which port the
// daemon picked, probing /v1/team/list, granting loopback tokens
// for write endpoints, switching teams, and importing the team's
// HMAC key for client-side decryption of hashed Resource fields.
//
// Why a single module: every loopback call goes through the same
// port-scan pattern + CORS fallback + (on Chrome 130+) Private
// Network Access prompt. Keeping them together lets one
// showPNAExplainerOnce() singleton serialize the explainer modal
// across all concurrent first-session callers — without it the user
// could see 2-3 stacked PNA overlays.
//
// Public exports:
//   probeLocalDaemon()              — populates state.localDaemon*
//                                      fields (called from boot +
//                                      renderAll); silent on failure.
//   switchLocalDaemonTeam(targetID) — banner CTA that flips the
//                                      daemon's bound team without
//                                      requiring a terminal.
//   fetchHmacKeyFromDaemon()        — fetch + AES-derive the team's
//                                      HMAC key. Throws on failure.
//   tryAutoImportHmacKey()          — silent variant; returns bool.
//                                      Called from boot + renderAll +
//                                      decrypt-banner-import branch.
//
// Module init wires the 3 Machines → File Path Decryption click handlers
// (#settings-hmac-save, -autoimport, -clear).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2t).
import { $ } from "./dom.js";
import { showToast } from "./ui.js";
import { state } from "./state.js";
import { deriveAESKey } from "./crypto.js";
import {
  updateSetupBanner,
  updateDecryptBanner,
} from "./banners.js";
import {
  updateDecryptStatus,
  showSettingsToast,
} from "./daemon-settings.js";

const LOCAL_DAEMON_PORT_MIN = 7777;
const LOCAL_DAEMON_PORT_MAX = 7790;

// showPNAExplainerOnce shows a one-time styled modal before the first
// loopback fetch fires. Without this, Chrome 130+ shows an unannotated
// "wants to access other apps and services on this device" PNA prompt
// that users have no context for — it sounds like spyware.
//
// The modal explains what's about to happen and why. Resolves once
// the user clicks "Allow" or "Skip"; either way we then proceed and
// let Chrome's native prompt fire (or not, if they'd already approved
// it for this origin).
//
// Stored in localStorage (not sessionStorage) — we want it to fire
// exactly once per user across all tabs/sessions, not every new tab.
// "Skip for now" still records the dismissal so we don't re-prompt;
// users can re-trigger by clearing site data.
//
// In-flight singleton: probeLocalDaemon, fetchHmacKeyFromDaemon, and
// ensureLoopbackToken can all race on first session — without this
// singleton, the user could see 2-3 stacked PNA overlays. The promise
// is shared so concurrent callers all await the same dismiss.
let _pnaExplainerPromise = null;
function showPNAExplainerOnce() {
  if (_pnaExplainerPromise) return _pnaExplainerPromise;
  _pnaExplainerPromise = new Promise(async (resolve) => {
    try {
      if (localStorage.getItem("ac-pna-explainer-shown") === "1") {
        resolve();
        return;
      }
    } catch {
      resolve();
      return;
    }
    // Chrome only asks while the permission is undecided; otherwise there is
    // nothing to explain and this would be a second box with nothing after it.
    if ((await localNetworkPermission()) !== "prompt") {
      resolve();
      return;
    }
    const overlay = document.createElement("div");
    overlay.style.cssText =
      "position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;";
    overlay.innerHTML =
      "" +
      '<div style="background:var(--bg-elevated);max-width:480px;width:100%;border-radius:12px;padding:28px;box-shadow:0 12px 40px rgba(0,0,0,0.4);">' +
      '<h2 style="margin:0 0 12px;font-size:20px;font-weight:600;">Your browser will ask one question next</h2>' +
      '<p style="margin:0 0 12px;color:var(--text-secondary);line-height:1.5;">' +
      "It asks to let this page reach AgentCollision on this computer. Allow it to see real chat names and file paths, switch teams, and get live activity." +
      "</p>" +
      '<p style="margin:0 0 20px;color:var(--text-secondary);line-height:1.5;">' +
      "Nothing leaves your computer: this page only talks to AgentCollision running on it." +
      "</p>" +
      '<div style="display:flex;gap:8px;justify-content:flex-end;">' +
      '<button id="ac-pna-skip" class="btn btn-sm" style="background:transparent;color:var(--text-secondary);">Skip for now</button>' +
      '<button id="ac-pna-allow" class="btn btn-sm" style="background:var(--accent);color:white;border:none;">Continue</button>' +
      "</div>" +
      "</div>";
    document.body.appendChild(overlay);
    const finish = () => {
      try {
        localStorage.setItem("ac-pna-explainer-shown", "1");
      } catch {}
      overlay.remove();
      resolve();
    };
    overlay.querySelector("#ac-pna-allow").addEventListener("click", finish);
    overlay.querySelector("#ac-pna-skip").addEventListener("click", finish);
  });
  return _pnaExplainerPromise;
}

// resolveLocalDaemonBase finds the http://127.0.0.1:NNNN base URL
// where the local daemon is actually listening. The daemon's
// pickHTTPAddress auto-falls-back through 7777-7790 when 7777 is
// taken (port conflicts with iRMC, Cloudera Hue, etc), so a fixed
// 7777 fetch silently fails for users who hit the fallback path.
//
// Probes /v1/healthz across the same 7777-7790 range as the daemon.
// First success wins. Cached in sessionStorage for the rest of the
// tab so we don't re-probe on every banner repaint.
//
// Returns '' if no daemon is reachable on any port — callers fall
// through to the "no daemon" UX path.
//
// CONSOLE NOISE (2026-09-02): a failed resolve used to emit FOURTEEN red
// lines — one CORS/PNA rejection on 7777 followed by thirteen
// ERR_CONNECTION_REFUSED as the loop walked 7778..7790. Those errors are
// logged by the browser itself, before any JS runs, so the `catch` below
// cannot suppress them; the only way to quiet the log is to make fewer
// requests.
//
// The obvious fix — "detect the PNA block on 7777 and stop scanning" — is
// NOT IMPLEMENTABLE, and I measured that rather than assuming it. Chrome
// reports a Private Network Access block and an unbound port as the SAME
// opaque `TypeError: Failed to fetch`, deliberately, so a page can't use
// fetch as a port scanner. Timing doesn't separate them either: measured in
// the live dashboard, both fail in under 1ms. There is no signal to branch on.
//
// So instead: remember the port that worked, across sessions.
//
//   - The daemon binds 7777 in the overwhelming majority of installs; the
//     7778..7790 walk exists only for port-conflict cases (iRMC, Cloudera Hue).
//   - localStorage (not sessionStorage) means the very first successful
//     resolve makes every LATER tab a single request, forever, instead of
//     re-walking the range each time.
//   - A remembered port that stops answering falls back to the full scan
//     once, then re-caches — so a daemon that moves ports still recovers.
//
// A negative result is cached briefly too (NEG_TTL): when there is genuinely
// no daemon, repeated banner repaints shouldn't each re-emit the full
// fourteen-line wall.
const DAEMON_BASE_KEY = "ac-local-daemon-base";
const DAEMON_NEG_KEY = "ac-local-daemon-none-until";
const NEG_TTL_MS = 60_000;

async function probeOne(base) {
  try {
    const r = await fetch(base + "/v1/healthz", {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    return !!r.ok;
  } catch {
    // Indistinguishable by design: port not bound, CORS rejection, PNA
    // block. See the note above — do not try to branch on this.
    return false;
  }
}

export async function resolveLocalDaemonBase() {
  // A page served from loopback IS the daemon: no scan, no permission explainer.
  try {
    const h = (location.hostname || "").toLowerCase();
    if (h === "localhost" || h === "127.0.0.1" || h === "::1") return location.origin;
  } catch {}
  // 1. A port that worked before. One request in the common case.
  let remembered = "";
  try {
    remembered =
      localStorage.getItem(DAEMON_BASE_KEY) ||
      sessionStorage.getItem(DAEMON_BASE_KEY) ||
      "";
  } catch {}
  if (remembered && (await probeOne(remembered))) return remembered;

  // 2. Recently established that nothing is listening — don't re-scan (and
  //    don't re-spam the console) on every banner repaint.
  try {
    const until = parseInt(localStorage.getItem(DAEMON_NEG_KEY) || "0", 10);
    if (Number.isFinite(until) && Date.now() < until) return "";
  } catch {}

  // First attempt of the session — let users know what's about to
  // happen. No-op once it's been shown once.
  await showPNAExplainerOnce();
  for (let p = LOCAL_DAEMON_PORT_MIN; p <= LOCAL_DAEMON_PORT_MAX; p++) {
    const base = "http://127.0.0.1:" + p;
    if (base === remembered) continue; // already tried above
    if (await probeOne(base)) {
      try {
        localStorage.setItem(DAEMON_BASE_KEY, base);
        sessionStorage.setItem(DAEMON_BASE_KEY, base);
        localStorage.removeItem(DAEMON_NEG_KEY);
      } catch {}
      return base;
    }
  }
  try {
    localStorage.setItem(DAEMON_NEG_KEY, String(Date.now() + NEG_TTL_MS));
  } catch {}
  return "";
}

// probeLocalDaemon does a best-effort fetch to /v1/team/list to
// learn what teams this machine's daemon knows about + which is
// current. Silent on failure (no daemon, wrong port, PNA blocked,
// CORS rejected) — we just don't get richer banner state.
//
// State captured:
//   state.localDaemonCurrentTeam — id of bound team (empty = solo)
//   state.localDaemonCurrentTeamName — display name of same
//   state.localDaemonTeamIDs       — Set of all known team ids
export async function probeLocalDaemon() {
  state.localDaemonCurrentTeam = "";
  state.localDaemonCurrentTeamName = "";
  state.localDaemonTeamIDs = new Set();
  const base = await resolveLocalDaemonBase();
  if (!base) return;
  try {
    const r = await fetch(base + "/v1/team/list", {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return;
    const body = await r.json();
    const teams = (body && body.teams) || [];
    state.localDaemonTeamIDs = new Set(teams.map((t) => t.id));
    if (body.current) {
      state.localDaemonCurrentTeam = body.current;
      const cur = teams.find((t) => t.id === body.current);
      state.localDaemonCurrentTeamName = (cur && cur.display_name) || "";
    }
  } catch {
    // expected when no daemon is running locally
  }
}

// ensureLoopbackToken returns the daemon's per-install secret needed
// for write endpoints (POST /v1/team/switch etc). Cached in
// sessionStorage for this tab so we hit the grant endpoint at most
// once per browser session. Clears on signOut.
async function ensureLoopbackToken() {
  let tok = "";
  try {
    tok = sessionStorage.getItem("ac-loopback-token") || "";
  } catch {}
  if (tok) return tok;
  const base = await resolveLocalDaemonBase();
  if (!base) return "";
  try {
    const r = await fetch(base + "/v1/loopback/grant", {
      method: "GET",
      mode: "cors",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    if (!r.ok) return "";
    const body = await r.json();
    tok = (body && body.loopback_token) || "";
    if (tok) {
      try {
        sessionStorage.setItem("ac-loopback-token", tok);
      } catch {}
    }
    return tok;
  } catch {
    return "";
  }
}

// switchLocalDaemonTeam tells the local daemon to flip its current
// team to the team this dashboard is viewing. One-click; no terminal
// dance. Used by the banner's "Switch this laptop to this team" CTA.
export async function switchLocalDaemonTeam(targetTeamID) {
  const tok = await ensureLoopbackToken();
  if (!tok) {
    showToast(
      "error",
      "Couldn’t reach the daemon (is `agc daemon` running?). Try `agc team switch` from a terminal.",
    );
    return;
  }
  const base = await resolveLocalDaemonBase();
  if (!base) {
    showToast(
      "error",
      "Lost the daemon connection. Try `agc team switch` from a terminal.",
    );
    return;
  }
  const r = await fetch(base + "/v1/team/switch", {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      "X-Loopback-Token": tok,
    },
    body: JSON.stringify({ team_id: targetTeamID }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    showToast("error", "Switch failed: " + (e.message || "HTTP " + r.status));
    return;
  }
  showToast(
    "success",
    "Switched daemon to this team. Activity will appear shortly.",
  );
  // Re-probe immediately so the banner updates without waiting for
  // the next polling tick.
  await probeLocalDaemon();
  updateSetupBanner();
}

// fetchHmacKeyFromDaemon tries the key endpoint directly across the
// daemon's port range, instead of going through resolveLocalDaemonBase()
// which probes /v1/healthz first. /v1/healthz currently doesn't return
// CORS headers for hosted origins, so the probe fails and the silent
// import bails — even though /v1/team/hmac-key itself works. By going
// straight to the real endpoint we eliminate that whole class of
// failure. First port that returns 200 wins; subsequent ones are
// skipped.
//
// Throws on any failure — daemon not reachable on any port, endpoint
// missing, malformed key. Callers decide whether to surface the
// error (manual button) or swallow it (silent boot-time auto-import).
//
// Daemon's HTTP listener is loopback-only (127.0.0.1:7777-7790) so
// the key never leaves the user's machine — even though the dashboard
// is hosted, fetch() to localhost from this origin hits ONLY the
// user's own daemon, never anyone else's.
export async function fetchHmacKeyFromDaemon() {
  // Honor the cached base from resolveLocalDaemonBase() if it's there
  // — saves a port scan on the second call.
  let cached = "";
  try {
    cached = sessionStorage.getItem("ac-local-daemon-base") || "";
  } catch {}
  // First-attempt PNA explainer: only fire when we have to scan
  // (cached path is already past the explainer point). Skip
  // entirely when the page is already loopback-served — the user
  // implicitly granted "talk to your local daemon" by visiting
  // http://localhost:NNNN; flashing a modal there is noise.
  const onLoopback = (() => {
    try {
      const h = (location.hostname || "").toLowerCase();
      return h === "localhost" || h === "127.0.0.1" || h === "::1";
    } catch {
      return false;
    }
  })();
  // A loopback-served page IS the daemon: ask it, not 13 other ports.
  const candidates = cached
    ? [cached]
    : onLoopback
      ? [location.origin]
      : Array.from(
          { length: LOCAL_DAEMON_PORT_MAX - LOCAL_DAEMON_PORT_MIN + 1 },
          (_, i) => "http://127.0.0.1:" + (LOCAL_DAEMON_PORT_MIN + i),
        );
  if (!cached && !onLoopback) await showPNAExplainerOnce();

  let lastErr = null;
  for (const base of candidates) {
    try {
      const r = await fetch(base + "/v1/team/hmac-key", {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      if (!r.ok) {
        // Could be the real daemon rejecting (e.g. 403 from older
        // version) OR an unrelated server that happens to bind this
        // port. Save the error and try the next port — only success
        // ends the scan.
        let msg = "HTTP " + r.status;
        try {
          const e = await r.json();
          msg = e.message || e.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const body = await r.json();
      if (!body.hmac_key_b64) throw new Error("Daemon returned no key.");
      const key = await deriveAESKey(body.hmac_key_b64);
      if (!key) throw new Error("Key from daemon was malformed.");
      // Cache the working base so subsequent calls (and other
      // resolveLocalDaemonBase() consumers) skip the port scan.
      try {
        sessionStorage.setItem("ac-local-daemon-base", base);
      } catch {}
      return { key, keyB64: body.hmac_key_b64 };
    } catch (e) {
      // Port not bound, CORS rejection, PNA block — try the next.
      // We only break out of the loop on a definite "found but
      // rejected" (handled above by re-throwing inside the try).
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to fetch");
}

// tryAutoImportHmacKey runs fetchHmacKeyFromDaemon silently and
// stores the result on success. Returns true if the key is now
// loaded (either from this call or because it was already loaded).
// Never throws.
export async function tryAutoImportHmacKey() {
  if (state.hmacKey) return true;
  try {
    const { key, keyB64 } = await fetchHmacKeyFromDaemon();
    state.hmacKey = key;
    state.hmacKeyB64 = keyB64;
    state.localNetworkBlocked = false;
    // Safari private mode can throw on setItem — but if it does, the
    // key still works for this session, so swallow.
    try {
      localStorage.setItem("ac-hmac-key", keyB64);
    } catch {}
    updateDecryptStatus();
    return true;
  } catch {
    state.localNetworkBlocked = !(await localNetworkAllowed());
    updateDecryptBanner();
    return false;
  }
}

// Chrome asks before a website may reach this computer. Until that is
// allowed every key fetch fails as a bare "Failed to fetch", so read the
// permission to tell the person what to click. Browsers without it
// (Safari, older Chrome) report allowed and keep the generic banner.
async function localNetworkAllowed() {
  const p = await localNetworkPermission();
  return p === "" || p === "granted";
}

// localNetworkPermission returns Chrome's state for reaching this computer
// ("granted", "prompt", "denied"), or "" when the browser has no such
// permission.
async function localNetworkPermission() {
  if (!navigator.permissions) return "";
  for (const name of ["loopback-network", "local-network-access"]) {
    try {
      return (await navigator.permissions.query({ name })).state;
    } catch {}
  }
  return "";
}

// ---- module init: Machines → File Path Decryption click handlers ----

function wireListeners() {
  // Manual paste of the base64 key.
  if ($("settings-hmac-save")) {
    $("settings-hmac-save").addEventListener("click", async () => {
      const keyB64 = $("settings-hmac-input").value.trim();
      if (!keyB64) return;
      const key = await deriveAESKey(keyB64);
      if (!key) {
        showToast(
          "error",
          "Invalid key format. Expected base64-encoded 32-byte key.",
        );
        return;
      }
      state.hmacKey = key;
      state.hmacKeyB64 = keyB64;
      localStorage.setItem("ac-hmac-key", keyB64);
      $("settings-hmac-input").value = "";
      updateDecryptStatus();
      updateDecryptBanner();
      showSettingsToast();
    });
  }

  // Auto-import from local daemon: same as the decrypt-banner-import
  // CTA but surfaces detailed error messages (PNA / 403 / network /
  // unknown) since this is the explicit "I'm troubleshooting" entry
  // point.
  if ($("settings-hmac-autoimport")) {
    $("settings-hmac-autoimport").addEventListener("click", async () => {
      const btn = $("settings-hmac-autoimport");
      const prev = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Importing…";
      try {
        const { key, keyB64 } = await fetchHmacKeyFromDaemon();
        state.hmacKey = key;
        state.hmacKeyB64 = keyB64;
        localStorage.setItem("ac-hmac-key", keyB64);
        updateDecryptStatus();
        updateDecryptBanner();
        showToast(
          "success",
          "Imported the team key from this computer. File names will now show in full.",
        );
      } catch (e) {
        // Two common failure shapes:
        //   - "Failed to fetch" / no daemon found across 7777-7790 →
        //     daemon not running, all candidate ports busy, or
        //     local network blocks loopback for hosted-origin fetches.
        //   - 403 forbidden → daemon is older than v0.12.0 and lacks
        //     this endpoint, or origin allowlist mismatch.
        const msg = (e && e.message) || "unknown";
        state.localNetworkBlocked = !(await localNetworkAllowed());
        updateDecryptBanner();
        if (state.localNetworkBlocked) {
          showToast(
            "error",
            "Your browser blocked this page from reaching your computer. Click the icon left of the address bar, turn on Apps on device (Local network access in older Chrome), then reload.",
          );
        } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
          showToast(
            "error",
            "Could not reach local daemon. Is `agc daemon` running on this machine? Try `agc status` to verify.",
          );
        } else if (msg.includes("403")) {
          showToast(
            "error",
            "Daemon refused (403). You may need to upgrade with `brew upgrade agentcollision/tap/agentcollision`.",
          );
        } else {
          showToast("error", "Import failed: " + msg);
        }
      } finally {
        btn.disabled = false;
        btn.textContent = prev;
      }
    });
  }

  // Clear the loaded key. Banner re-surfaces (per-session dismiss
  // flag reset) so the user has a path back if they wanted decryption.
  if ($("settings-hmac-clear")) {
    $("settings-hmac-clear").addEventListener("click", () => {
      state.hmacKey = null;
      state.hmacKeyB64 = null;
      localStorage.removeItem("ac-hmac-key");
      updateDecryptStatus();
      // After clearing, surface the banner again so the user has a
      // path back. Reset the per-session dismiss flag too — the user
      // just affirmatively removed the key, so the banner is
      // informative now.
      window._decryptBannerDismissed = false;
      updateDecryptBanner();
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
